/**
 * FSD (Supervised) market availability — curated from Tesla support + community trackers.
 * @see https://www.tesla.com/support/full-self-driving-subscriptions
 * @see https://fsdtracker.eu/
 */
export type FsdAvailabilityStatus = 'active' | 'limited' | 'pending'

export interface FsdMarketRegion {
  id: string
  name: string
  status: FsdAvailabilityStatus
  /** Rough [west, south, east, north] for map fill */
  bbox: [number, number, number, number]
  notes?: string
  sources?: string[]
}

export const FSD_AVAILABILITY_META = {
  updatedAt: '2026-06-30',
  primarySource: 'https://www.tesla.com/support/full-self-driving-subscriptions',
  secondarySources: ['https://fsdtracker.eu/', 'https://www.notateslaapp.com/'],
  disclaimer:
    'Regulatory status changes frequently. Active = Tesla lists FSD subscription as currently available. Limited = approved but restricted rollout. Pending = approval or rollout in progress.',
}

/** Tesla official "currently available" + closely tracked pending/limited markets. */
export const FSD_MARKET_REGIONS: FsdMarketRegion[] = [
  // —— Active (Tesla support bullet list, Jun 2026) ——
  { id: 'us', name: 'United States', status: 'active', bbox: [-125, 24, -66, 50], sources: ['tesla_support'] },
  { id: 'ca', name: 'Canada', status: 'active', bbox: [-141, 41, -52, 84], sources: ['tesla_support'] },
  { id: 'mx', name: 'Mexico', status: 'active', bbox: [-118, 14, -86, 33], sources: ['tesla_support'] },
  { id: 'pr', name: 'Puerto Rico', status: 'active', bbox: [-68, 17.8, -65.2, 18.6], sources: ['tesla_support'] },
  { id: 'au', name: 'Australia', status: 'active', bbox: [113, -44, 154, -10], sources: ['tesla_support'] },
  { id: 'nz', name: 'New Zealand', status: 'active', bbox: [166, -47.5, 179, -34], sources: ['tesla_support'] },
  { id: 'nl', name: 'Netherlands', status: 'active', bbox: [3.2, 50.7, 7.3, 53.7], notes: 'First EU type approval (RDW)', sources: ['tesla_support', 'fsdtracker_eu'] },
  { id: 'lt', name: 'Lithuania', status: 'active', bbox: [20.9, 53.9, 26.9, 56.5], sources: ['tesla_support', 'fsdtracker_eu'] },
  { id: 'ee', name: 'Estonia', status: 'active', bbox: [21.8, 57.5, 28.2, 59.7], sources: ['tesla_support', 'fsdtracker_eu'] },
  { id: 'dk', name: 'Denmark', status: 'active', bbox: [8, 54.5, 15.2, 57.8], sources: ['tesla_support', 'fsdtracker_eu'] },
  { id: 'be', name: 'Belgium', status: 'active', bbox: [2.5, 49.4, 6.4, 51.6], sources: ['tesla_support', 'fsdtracker_eu'] },

  // —— Limited rollout ——
  {
    id: 'cn',
    name: 'China',
    status: 'limited',
    bbox: [73, 18, 135, 54],
    notes: 'FSD launched v13 to select owners; wider rollout pending local data rules',
    sources: ['tesla_support', 'notateslaapp'],
  },

  // —— Pending (EU tracker + Tesla pending lists) ——
  { id: 'de', name: 'Germany', status: 'pending', bbox: [5.8, 47.2, 15.1, 55.1], sources: ['fsdtracker_eu'] },
  { id: 'fr', name: 'France', status: 'pending', bbox: [-5.2, 41.3, 9.6, 51.1], sources: ['fsdtracker_eu'] },
  { id: 'gb', name: 'United Kingdom', status: 'pending', bbox: [-8.7, 49.8, 1.8, 60.9], sources: ['fsdtracker_eu'] },
  { id: 'es', name: 'Spain', status: 'pending', bbox: [-9.4, 36, 4.3, 43.8], sources: ['fsdtracker_eu'] },
  { id: 'it', name: 'Italy', status: 'pending', bbox: [6.6, 36.6, 18.5, 47.1], sources: ['fsdtracker_eu'] },
  { id: 'no', name: 'Norway', status: 'pending', bbox: [4.5, 58, 31.2, 71.2], sources: ['fsdtracker_eu'] },
  { id: 'se', name: 'Sweden', status: 'pending', bbox: [11, 55.3, 24.2, 69.1], sources: ['fsdtracker_eu'] },
  { id: 'pl', name: 'Poland', status: 'pending', bbox: [14.1, 49, 24.2, 54.9], sources: ['notateslaapp'] },
  { id: 'at', name: 'Austria', status: 'pending', bbox: [9.5, 46.4, 17.2, 49], sources: ['notateslaapp'] },
  { id: 'ie', name: 'Ireland', status: 'pending', bbox: [-10.7, 51.4, -5.4, 55.4], notes: 'Tesla in talks per community reports', sources: ['notateslaapp'] },
  { id: 'ch', name: 'Switzerland', status: 'pending', bbox: [5.9, 45.8, 10.5, 47.8], sources: ['tesla_support'] },
  { id: 'kr', name: 'South Korea', status: 'pending', bbox: [124.6, 33.1, 131.9, 38.6], notes: 'Tesla subscription page live; rollout expected', sources: ['tesla_support'] },
  { id: 'jp', name: 'Japan', status: 'pending', bbox: [129, 30, 146, 46], notes: 'Tesla targeting launch', sources: ['notateslaapp'] },
  { id: 'tw', name: 'Taiwan', status: 'pending', bbox: [119.5, 21.9, 122.1, 25.3], sources: ['tesla_support'] },
  { id: 'sg', name: 'Singapore', status: 'pending', bbox: [103.6, 1.16, 104.1, 1.48], sources: ['tesla_support'] },
  { id: 'il', name: 'Israel', status: 'pending', bbox: [34.2, 29.5, 35.9, 33.3], sources: ['tesla_support'] },
  { id: 'ae', name: 'United Arab Emirates', status: 'pending', bbox: [51.5, 22.6, 56.4, 26.1], sources: ['tesla_support'] },
  { id: 'sa', name: 'Saudi Arabia', status: 'pending', bbox: [34.5, 16.3, 55.7, 32.2], sources: ['tesla_support'] },
  { id: 'cl', name: 'Chile', status: 'pending', bbox: [-75.7, -56, -66, -17.5], sources: ['notateslaapp'] },
  { id: 'co', name: 'Colombia', status: 'pending', bbox: [-79, -4.2, -66.8, 13.5], sources: ['notateslaapp'] },
  { id: 'br', name: 'Brazil', status: 'pending', bbox: [-74, -34, -34, 5.3], sources: ['notateslaapp'] },
]

export function bboxToPolygon(bbox: [number, number, number, number]): [number, number][] {
  const [w, s, e, n] = bbox
  return [[w, s], [e, s], [e, n], [w, n], [w, s]]
}